import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Shield, X } from 'lucide-react';

const STORAGE_KEY = 'sipl_dpdp_consent_v1';
const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Generate (or reuse) an anonymous principal id for this visitor.
 * Stored in localStorage so repeat visits map to the same consent principal.
 */
const getOrCreatePrincipalId = () => {
  let pid = localStorage.getItem('sipl_principal_id');
  if (!pid) {
    pid = `anon_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem('sipl_principal_id', pid);
  }
  return pid;
};

const collectDeviceInfo = () => ({
  platform: typeof navigator !== 'undefined' ? navigator.platform : 'unknown',
  ua: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
  viewport:
    typeof window !== 'undefined'
      ? `${window.innerWidth}x${window.innerHeight}`
      : 'unknown',
  language: typeof navigator !== 'undefined' ? navigator.language : 'unknown',
});

/**
 * Record a consent decision via backend proxy.
 * Returns the response payload (or null on failure).
 */
export const recordConsent = async ({
  principal_id,
  principal_email,
  principal_name,
  source_application = 'sanghvi-website',
  channel = 'website',
} = {}) => {
  try {
    const pid = principal_id || getOrCreatePrincipalId();
    const res = await fetch(`${API}/api/consent/collect`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        principal_id: pid,
        principal_email,
        principal_name,
        source_application,
        channel,
        device_info: collectDeviceInfo(),
      }),
    });
    const data = await res.json();
    return data;
  } catch (err) {
    console.error('Consent recording failed', err);
    return null;
  }
};

const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      // Delay slightly so it doesn't fight the initial page paint
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const handleDecision = async (decision) => {
    setSubmitting(true);
    const record = {
      decision,
      recorded_at: new Date().toISOString(),
    };

    if (decision === 'accept') {
      const result = await recordConsent({
        source_application: 'sanghvi-website',
        channel: 'website',
      });
      if (result?.success && result?.data?.id) {
        record.consent_id = result.data.id;
      }
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    setSubmitting(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed bottom-0 left-0 right-0 z-[60] px-4 pb-4 md:px-6 md:pb-6 animate-in slide-in-from-bottom duration-500"
    >
      <div className="max-w-6xl mx-auto bg-slate-950 text-white rounded-2xl shadow-2xl border border-slate-800 overflow-hidden">
        <div className="p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-start gap-4 flex-1">
            <div className="w-11 h-11 rounded-xl bg-cyan-500/15 border border-cyan-400/30 flex items-center justify-center flex-shrink-0">
              <Shield className="h-5 w-5 text-cyan-300" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base md:text-lg font-semibold mb-1">
                Your Privacy & Consent (DPDPA)
              </h3>
              <p className="text-sm text-slate-300 leading-relaxed">
                We use cookies and process limited personal data to improve your
                experience, respond to enquiries and measure site performance —
                in line with India&apos;s Digital Personal Data Protection Act
                (DPDPA).{' '}
                <Link
                  to="/privacy-policy"
                  className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2"
                >
                  Read our privacy policy
                </Link>
                .
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 md:gap-3 md:items-center flex-shrink-0">
            <Button
              data-testid="consent-reject"
              variant="outline"
              disabled={submitting}
              onClick={() => handleDecision('reject')}
              className="border-slate-600 text-white bg-transparent hover:bg-slate-800 hover:text-white"
            >
              Reject
            </Button>
            <Button
              data-testid="consent-accept"
              disabled={submitting}
              onClick={() => handleDecision('accept')}
              className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-semibold"
            >
              {submitting ? 'Saving...' : 'Accept & Continue'}
            </Button>
            <button
              data-testid="consent-close"
              aria-label="Dismiss"
              disabled={submitting}
              onClick={() => handleDecision('dismiss')}
              className="hidden md:flex w-8 h-8 items-center justify-center rounded-full text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
